import { useState, useEffect } from 'react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  Button,
  CircularProgress,
  Typography,
  Chip,
  Box,
  Avatar,
  Card,
  CardContent,
  Grid,
  Fade,
  Zoom,
  Alert,
  Divider,
  IconButton,
  Tooltip,
  Tabs,
  Tab
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
  Badge as BadgeIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon,
  Group as GroupIcon,
  Verified as VerifiedIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import coursesService from '../../../../services/CoursesService';
import UserProfileService from '../../../../services/UserProfileService';
import UsersService from '../../../../services/UsersService';

/** Primer string no vacío (API puede usar camelCase o PascalCase). */
const pickFirstNonEmpty = (...values) => {
  for (const v of values) {
    if (v == null) continue;
    const s = typeof v === 'string' ? v.trim() : String(v).trim();
    if (s !== '') return s;
  }
  return '';
};

const buildNameFromParts = (obj) => {
  if (!obj || typeof obj !== 'object') return '';
  const a = pickFirstNonEmpty(obj.firstName, obj.FirstName, obj.givenName, obj.GivenName);
  const b = pickFirstNonEmpty(obj.lastName, obj.LastName, obj.surname, obj.Surname, obj.familyName);
  return pickFirstNonEmpty(`${a} ${b}`.trim(), a, b);
};

/**
 * Normaliza nombre y cédula desde la respuesta de solicitudes pendientes.
 * El backend a veces anida `user`, otras devuelve campos planos o en PascalCase.
 */
const resolvePendingRequestStudent = (request) => {
  if (!request || typeof request !== 'object') {
    return { name: 'Sin datos', cedula: '—', id: null };
  }

  const u = request.user ?? request.User;

  let name = '';

  if (u && typeof u === 'object') {
    name = pickFirstNonEmpty(
      buildNameFromParts(u),
      u.name,
      u.Name,
      u.fullName,
      u.FullName,
      u.displayName,
      u.DisplayName,
      u.userName,
      u.UserName,
      u.nombre,
      u.Nombre,
      u.email,
      u.Email
    );
  }

  name = pickFirstNonEmpty(
    name,
    buildNameFromParts(request),
    request.studentName,
    request.userName,
    request.Name,
    request.fullName,
    request.FullName,
    request.applicantName,
    request.ApplicantName,
    request.StudentName,
    request.UserName,
    request.student?.name,
    request.Student?.Name
  );

  const cedula = pickFirstNonEmpty(
    u?.cedula,
    u?.Cedula,
    u?.documentNumber,
    u?.DocumentNumber,
    request.studentCedula,
    request.userCedula,
    request.Cedula,
    request.cedula,
    request.documentNumber,
    request.DocumentNumber
  );

  const id =
    request.userId ??
    request.studentId ??
    request.UserId ??
    request.StudentId ??
    request.applicationUserId ??
    request.ApplicationUserId ??
    u?.id ??
    u?.Id ??
    request.id;

  if (!name) {
    if (cedula) name = `Estudiante (C.C. ${cedula})`;
    else if (id != null && id !== '') name = `Usuario #${id}`;
    else name = 'Estudiante';
  }

  return {
    name,
    cedula: cedula || 'Sin cédula',
    id: id ?? request.id ?? 'N/A',
  };
};

/** userId de la solicitud (el API de pendientes a veces solo envía ids, sin objeto user). */
const extractRequestUserId = (r) => {
  if (!r || typeof r !== 'object') return null;
  const uid =
    r.userId ??
    r.UserId ??
    r.studentId ??
    r.StudentId ??
    r.applicationUserId ??
    r.ApplicationUserId ??
    (r.user && typeof r.user === 'object' ? (r.user.id ?? r.user.Id) : null) ??
    (r.User && typeof r.User === 'object' ? (r.User.id ?? r.User.Id) : null);
  return uid != null && uid !== '' ? uid : null;
};

/**
 * Completa cada solicitud con GET /User/:id cuando falta nombre/cédula en la respuesta.
 */
const enrichRequestsWithUserProfiles = async (requests) => {
  if (!Array.isArray(requests) || requests.length === 0) return requests;

  const needsProfile = (r) => {
    const uid = extractRequestUserId(r);
    if (uid == null) return false;
    const resolved = resolvePendingRequestStudent(r);
    const placeholderName =
      !resolved.name ||
      resolved.name.startsWith('Usuario #') ||
      resolved.name === 'Estudiante' ||
      resolved.name === 'Sin datos';
    const missingCedula = !resolved.cedula || resolved.cedula === 'Sin cédula';
    return placeholderName || missingCedula;
  };

  const ids = new Set();
  for (const r of requests) {
    if (!needsProfile(r)) continue;
    const uid = extractRequestUserId(r);
    if (uid != null) ids.add(uid);
  }

  if (ids.size === 0) return requests;

  const service = new UserProfileService();
  const profileById = {};
  await Promise.all(
    [...ids].map(async (id) => {
      try {
        profileById[id] = await service.getUserById(id);
      } catch {
        try {
          profileById[id] = await UsersService.getUser(id);
        } catch {
          profileById[id] = null;
        }
      }
    })
  );

  return requests.map((r) => {
    if (!needsProfile(r)) return r;
    const uid = extractRequestUserId(r);
    const profile = uid != null ? profileById[uid] : null;
    if (!profile) return r;
    const prev = r.user && typeof r.user === 'object' ? r.user : {};
    return { ...r, user: { ...prev, ...profile } };
  });
};

const AssignCourseForm = ({ courseId, onClose }) => {
  const [pendingRequests, setPendingRequests] = useState([]);
  const [approvedRequests, setApprovedRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState({}); // Para manejar estados de carga por solicitud
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    const fetchRequests = async () => {
      try {
        setLoading(true);
        const [pendingData, approvedData] = await Promise.all([
          coursesService.getCoursePendingRequests(courseId),
          coursesService.getCourseApprovedRequests(courseId)
        ]);
        
        // Normalizar los datos de las solicitudes aprobadas
        const normalizedApprovedData = approvedData.map(item => {
          // Si el item ya tiene la estructura correcta (con user), lo devolvemos tal como está
          if (item.user) {
            return { ...item, status: 1 };
          }
          
          // Si el item es directamente un usuario (sin estructura de solicitud)
          if (item.name && item.cedula) {
            return {
              id: item.id || Math.random(), // Generar un ID si no existe
              user: {
                name: item.name,
                cedula: item.cedula,
                id: item.id
              },
              status: 1,
              isApproved: true
            };
          }
          
          // Si no podemos normalizar, devolvemos null para filtrarlo
          return null;
        }).filter(item => item !== null);
        
        // Ensure data is an array
        const safePendingData = Array.isArray(pendingData) ? pendingData : [];
        const safeApprovedData = Array.isArray(normalizedApprovedData) ? normalizedApprovedData : [];

        const [enrichedPending, enrichedApproved] = await Promise.all([
          enrichRequestsWithUserProfiles(safePendingData),
          enrichRequestsWithUserProfiles(safeApprovedData),
        ]);

        setPendingRequests(enrichedPending);
        setApprovedRequests(enrichedApproved);
      } catch (err) {
        setError('Error al cargar las solicitudes');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchRequests();
  }, [courseId]);

  const handleApprove = async (requestId) => {
    try {
      setProcessing(prev => ({ ...prev, [requestId]: 'approving' }));
      
      // Llamar al servicio para aprobar la solicitud
      await coursesService.approveRequest(requestId);
      
      // Mover la solicitud de pendiente a aprobada
      const approvedRequest = pendingRequests.find(req => req.id === requestId);
      if (approvedRequest) {
        setPendingRequests(pendingRequests.filter(req => req.id !== requestId));
        setApprovedRequests([...approvedRequests, { ...approvedRequest, status: 1 }]);
      }
    } catch (err) {
      console.error('Error al aprobar la solicitud:', err);
      
      // Mostrar mensajes de error más específicos
      let errorMessage = 'Error al aprobar la solicitud';
      if (err.message?.includes('Usuario no encontrado en localStorage')) {
        errorMessage = 'Sesión expirada. Por favor, inicia sesión nuevamente.';
      } else if (err.message?.includes('No se pudo obtener el ID del profesor del localStorage')) {
        errorMessage = 'Error de autenticación. Por favor, inicia sesión nuevamente.';
      } else if (err.response?.status === 401) {
        errorMessage = 'No tienes permisos para realizar esta acción.';
      } else if (err.response?.status === 404) {
        errorMessage = 'La solicitud no fue encontrada.';
      } else if (err.response?.status === 400) {
        errorMessage = 'Datos inválidos para aprobar la solicitud.';
      }
      
      setError(errorMessage);
    } finally {
      setProcessing(prev => ({ ...prev, [requestId]: false }));
    }
  };

  const handleReject = async (requestId) => {
    try {
      setProcessing(prev => ({ ...prev, [requestId]: 'rejecting' }));
      
      // Llamar al servicio para rechazar la solicitud
      await coursesService.denyRequest(requestId);
      
      // Eliminar la solicitud rechazada
      setPendingRequests(pendingRequests.filter(req => req.id !== requestId));
    } catch (err) {
      console.error('Error al rechazar la solicitud:', err);
      
      // Mostrar mensajes de error más específicos
      let errorMessage = 'Error al rechazar la solicitud';
      if (err.message?.includes('Usuario no encontrado en localStorage')) {
        errorMessage = 'Sesión expirada. Por favor, inicia sesión nuevamente.';
      } else if (err.message?.includes('No se pudo obtener el ID del profesor del localStorage')) {
        errorMessage = 'Error de autenticación. Por favor, inicia sesión nuevamente.';
      } else if (err.response?.status === 401) {
        errorMessage = 'No tienes permisos para realizar esta acción.';
      } else if (err.response?.status === 404) {
        errorMessage = 'La solicitud no fue encontrada.';
      } else if (err.response?.status === 400) {
        errorMessage = 'Datos inválidos para rechazar la solicitud.';
      }
      
      setError(errorMessage);
    } finally {
      setProcessing(prev => ({ ...prev, [requestId]: false }));
    }
  };

  const getStatusColor = (status) => {
    return status === 0 ? 'warning' : 'success';
  };

  const getStatusText = (status) => {
    return status === 0 ? 'Pendiente' : 'Aprobado';
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '200px',
        flexDirection: 'column',
        gap: 2
      }}>
        <CircularProgress 
          size={60}
          sx={{ 
            color: '#4DABA4',
            '& .MuiCircularProgress-circle': {
              strokeLinecap: 'round',
            }
          }} 
        />
        <Typography variant="h6" color="textSecondary">
          Cargando solicitudes...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Zoom in={true}>
        <Alert 
          severity="error" 
          sx={{ 
            borderRadius: '12px',
            '& .MuiAlert-icon': {
              fontSize: '2rem'
            }
          }}
        >
          <Typography variant="h6">{error}</Typography>
        </Alert>
      </Zoom>
    );
  }

  const totalRequests = pendingRequests.length + approvedRequests.length;

  if (totalRequests === 0) {
    return (
      <Zoom in={true}>
        <Box sx={{ 
          textAlign: 'center', 
          py: 6,
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          borderRadius: '16px',
          border: '2px dashed #4DABA4'
        }}>
          <CheckCircleIcon sx={{ fontSize: 80, color: '#4DABA4', mb: 2 }} />
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold', color: '#2E8A82' }}>
            ¡Excelente!
          </Typography>
          <Typography variant="body1" color="textSecondary" sx={{ maxWidth: '400px', mx: 'auto' }}>
            No hay solicitudes de estudiantes para este curso.
          </Typography>
        </Box>
      </Zoom>
    );
  }

  const renderRequestsTable = (requests, title, emptyMessage, showActions = true) => (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#2E8A82', display: 'flex', alignItems: 'center', gap: 1 }}>
        {title === 'Pendientes' ? <ScheduleIcon /> : <VerifiedIcon />}
        {title} ({requests.length})
      </Typography>
      
      {requests.length === 0 ? (
        <Box sx={{ 
          textAlign: 'center', 
          py: 4,
          background: 'rgba(77, 171, 164, 0.05)',
          borderRadius: '12px',
          border: '1px dashed rgba(77, 171, 164, 0.3)'
        }}>
          <Typography variant="body1" color="textSecondary">
            {emptyMessage}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ 
          background: 'white', 
          borderRadius: '16px',
          overflow: 'hidden',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          border: '1px solid rgba(77, 171, 164, 0.1)'
        }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ 
                  background: 'linear-gradient(45deg, #f8f9fa 30%, #e9ecef 90%)',
                  '& th': {
                    fontWeight: 'bold',
                    color: '#2E8A82',
                    borderBottom: '2px solid #4DABA4'
                  }
                }}>
                  <TableCell sx={{ pl: 3, pr: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonIcon sx={{ fontSize: 20 }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        Estudiante
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BadgeIcon sx={{ fontSize: 20 }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        Cédula
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ScheduleIcon sx={{ fontSize: 20 }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        Estado
                      </Typography>
                    </Box>
                  </TableCell>
                  {showActions && (
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                        <TrendingUpIcon sx={{ fontSize: 20 }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                          Acciones
                        </Typography>
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {requests.map((request, index) => {
                  if (!request) {
                    return null;
                  }

                  const userResolved = resolvePendingRequestStudent(request);
                  const userName = userResolved.name;
                  const userCedula = userResolved.cedula;
                  const userInitial = (userName.charAt(0) || '?').toUpperCase();
                  
                  return (
                    <Fade in={true} timeout={300 + index * 100} key={request.id || index}>
                      <TableRow 
                        sx={{ 
                          '&:hover': {
                            background: 'rgba(77, 171, 164, 0.05)',
                            transform: 'scale(1.01)',
                            transition: 'all 0.3s ease'
                          },
                          '&:nth-of-type(even)': {
                            background: 'rgba(77, 171, 164, 0.02)'
                          }
                        }}
                      >
                        <TableCell sx={{ pl: 3, pr: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar 
                              sx={{ 
                                bgcolor: request.status === 0 ? '#4DABA4' : '#4CAF50',
                                width: 40,
                                height: 40,
                                fontSize: '1rem',
                                fontWeight: 'bold'
                              }}
                            >
                              {userInitial}
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#2E8A82' }}>
                                {userName}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                ID: {request.id ?? userResolved.id ?? 'N/A'}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {userCedula}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={getStatusText(request.status)}
                            color={getStatusColor(request.status)}
                            sx={{
                              fontWeight: 'bold',
                              borderRadius: '20px',
                              '& .MuiChip-label': {
                                px: 2
                              }
                            }}
                            icon={request.status === 0 ? <ScheduleIcon /> : <CheckCircleIcon />}
                          />
                        </TableCell>
                        {showActions && request.status === 0 && (
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                              <Tooltip title="Aprobar solicitud" arrow>
                                <IconButton
                                  color="success"
                                  size="medium"
                                  onClick={() => handleApprove(request.id)}
                                  disabled={processing[request.id]}
                                  sx={{
                                    background: 'linear-gradient(45deg, #4CAF50 30%, #66BB6A 90%)',
                                    color: 'white',
                                    '&:hover': {
                                      background: 'linear-gradient(45deg, #388E3C 30%, #4CAF50 90%)',
                                      transform: 'scale(1.1)',
                                    },
                                    '&:disabled': {
                                      background: '#ccc',
                                      color: '#666'
                                    },
                                    transition: 'all 0.3s ease'
                                  }}
                                >
                                  {processing[request.id] === 'approving' ? 
                                    <CircularProgress size={20} color="inherit" /> : 
                                    <CheckCircleIcon />
                                  }
                                </IconButton>
                              </Tooltip>
                              
                              <Tooltip title="Rechazar solicitud" arrow>
                                <IconButton
                                  color="error"
                                  size="medium"
                                  onClick={() => handleReject(request.id)}
                                  disabled={processing[request.id]}
                                  sx={{
                                    background: 'linear-gradient(45deg, #F44336 30%, #EF5350 90%)',
                                    color: 'white',
                                    '&:hover': {
                                      background: 'linear-gradient(45deg, #D32F2F 30%, #F44336 90%)',
                                      transform: 'scale(1.1)',
                                    },
                                    '&:disabled': {
                                      background: '#ccc',
                                      color: '#666'
                                    },
                                    transition: 'all 0.3s ease'
                                  }}
                                >
                                  {processing[request.id] === 'rejecting' ? 
                                    <CircularProgress size={20} color="inherit" /> : 
                                    <CancelIcon />
                                  }
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        )}
                        {showActions && request.status === 1 && (
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CheckCircleIcon sx={{ color: '#4CAF50', fontSize: 24 }} />
                              <Typography variant="caption" color="success.main" sx={{ fontWeight: 'bold' }}>
                                Aprobado
                              </Typography>
                            </Box>
                          </TableCell>
                        )}
                      </TableRow>
                    </Fade>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header con estadísticas */}
      <Card sx={{ 
        mb: 3, 
        background: 'linear-gradient(45deg, #4DABA4 30%, #7BCDC7 90%)',
        color: 'white',
        borderRadius: '16px',
        boxShadow: '0 8px 32px rgba(77, 171, 164, 0.3)'
      }}>
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ 
                  bgcolor: 'rgba(255,255,255,0.2)', 
                  width: 56, 
                  height: 56 
                }}>
                  <GroupIcon sx={{ fontSize: 28 }} />
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {totalRequests}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Total de solicitudes
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={8}>
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {pendingRequests.length}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                      Pendientes
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {approvedRequests.length}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                      Aprobadas
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {totalRequests}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                      Total
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {totalRequests > 0 ? Math.round((approvedRequests.length / totalRequests) * 100) : 0}%
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                      Completado
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabs para navegar entre solicitudes */}
      <Box sx={{ mb: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          sx={{
            '& .MuiTab-root': {
              fontWeight: 'bold',
              textTransform: 'none',
              fontSize: '1rem',
              minHeight: '48px'
            },
            '& .Mui-selected': {
              color: '#4DABA4 !important'
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#4DABA4',
              height: '3px'
            }
          }}
        >
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ScheduleIcon />
                Pendientes ({pendingRequests.length})
              </Box>
            }
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <VerifiedIcon />
                Aprobadas ({approvedRequests.length})
              </Box>
            }
          />
        </Tabs>
      </Box>

      {/* Contenido de las tabs */}
      <Box sx={{ mt: 2 }}>
        {activeTab === 0 && (
          <Fade in={true} timeout={300}>
            <div>
              {renderRequestsTable(
                pendingRequests, 
                'Pendientes', 
                'No hay solicitudes pendientes de aprobación.',
                true
              )}
            </div>
          </Fade>
        )}
        
        {activeTab === 1 && (
          <Fade in={true} timeout={300}>
            <div>
              {renderRequestsTable(
                approvedRequests, 
                'Aprobadas', 
                'No hay solicitudes aprobadas.',
                false
              )}
            </div>
          </Fade>
        )}
      </Box>

      {/* Footer con información adicional */}
      <Box sx={{ mt: 3, p: 2, background: 'rgba(77, 171, 164, 0.05)', borderRadius: '12px' }}>
        <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center' }}>
          💡 <strong>Consejo:</strong> Revisa cada solicitud cuidadosamente antes de aprobar o rechazar. 
          Los estudiantes recibirán una notificación automática sobre el estado de su solicitud.
        </Typography>
      </Box>
    </Box>
  );
};

export default AssignCourseForm;